import React, { cloneElement, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PostModel } from '../../data-layer/posts/types';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { Box } from '../../ui/layout/Box';
import { Stack } from '../../ui/layout/Stack';
import { PostAdCard } from '../../ui/posts/PostCard/PostAdCard';
import { PostCard, PostCardSkeleton } from '../../ui/posts/PostCard/PostCard';
import { PostContextMenu } from '../../ui/posts/PostContextMenu';
import { isSuperAdmin } from '../../utils/accessService';
import { EventSource } from '../../utils/analyticsService';
import { useMeta } from '../../utils/useMeta';
import { useCurrentUser } from '../users/useCurrentUser';
import { useCanonicalUrl } from '../utils/useCanonicalUrl';
import { WithLoadMore } from '../withLoadMore';
import { usePostCollection } from './usePostCollection';
import { withPostCallbacks } from './withPostCallbacks';

const PER_PAGE = 12;

const EnhancedPostCard = withPostCallbacks(PostCard, EventSource.FEED);

const EnhancedPostMenu = withPostCallbacks(PostContextMenu, EventSource.FEED);

const buildMeta = (canonicalUrl: string) => {
    if (canonicalUrl) {
        return {
            links: [{ rel: 'canonical', href: canonicalUrl }],
        };
    }

    return {};
};

export function PostFeed() {
    const [query] = useSearchParams();
    const collection = usePostCollection({
        namespace: 'feed',
        filters: {
            amount: PER_PAGE,
            push_payload: query.get('push_payload'),
            first_post_id: query.get('first_post_id'),
            no_log: query.get('no_log'),
        },
    });
    const currentUser = useCurrentUser();
    const canonicalUrl = useCanonicalUrl();

    useMeta(buildMeta(canonicalUrl));

    const getPost = useCallback(
        (index: number, item: PostModel) => (
            <Box width="100%">
                {item ? (
                    <EnhancedPostCard
                        post={item}
                        index={index}
                        isProductButtonEnabled={isSuperAdmin(currentUser?.role)}
                        renderMenu={(trigger) => (
                            <EnhancedPostMenu trigger={trigger} post={item} user={currentUser} />
                        )}
                    />
                ) : (
                    <PostCardSkeleton />
                )}
            </Box>
        ),
        [],
    );

    const itemHandler = useCallback(
        (index: number) => {
            const item = collection.items[index];
            if ((index + 1) % 10 === 0) {
                return (
                    <WithLoadMore width="100%" onLoadMore={collection.onLoadNext}>
                        <PostAdCard postIndex={index + 1} />
                        {getPost(index, item)}
                    </WithLoadMore>
                );
            }

            return getPost(index, item);
        },
        [collection, getPost],
    );

    return (
        <DashboardPage py={0}>
            <Stack
                width="100%"
                maxWidth="100%"
                count={collection.items.length}
                renderItem={(index) => cloneElement(itemHandler(index), { key: index })}
            />
        </DashboardPage>
    );
}
